import { useLoadingOverlay } from "@/Shared/Hooks/useLoadingOverlay";
import { useLocationsSaved } from "@/Shared/Locations/Hooks/useLocationsSaved";
import { useSavedLocationsService } from "@/Shared/Locations/Services/LocationsSavedService";
import { useShipmentService } from "@/Services/ShipmentService";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { ReactNode } from "react";
import { useUpdatedBOLDialog } from "../Dialogs/ViewShipmentsUpdatedBOLDialog";
import { locationsFindSavedLocation } from "@/Shared/Locations/Helpers/locationsFindSavedLocation";
import { useModifyAddressDialog } from "../Dialogs/ViewShipmentsModifyAddressDialog";
import { useLocationsCreateSavedLocationFromShipment } from "@/Shared/Locations/Hooks/useLocationsCreateSavedLocationFromShipment";

function findSavedLocationId(
  locationContext: LocationContext,
  shipment: Shipment,
  savedLocations: Array<SavedLocation>,
) {
  const location =
    locationContext === LocationContext.Pickup
      ? shipment.pickupLocation
      : shipment.deliveryLocation;

  return locationsFindSavedLocation(savedLocations, location)?.savedLocationId;
}

export function useViewShipmentsModifyAddressAfterBookingDialog(
  shipment: Shipment,
  locationContext: LocationContext,
): { show: () => Promise<boolean>; dialogs: ReactNode } {
  const modifyAddressDialog = useModifyAddressDialog();
  const updatedBOLDialog = useUpdatedBOLDialog();
  const shipmentService = useShipmentService();
  const savedLocationsService = useSavedLocationsService();
  const { setLoading, overlay: loadingOverlay } = useLoadingOverlay();
  const { savedLocations } = useLocationsSaved();
  const createSavedLocation = useLocationsCreateSavedLocationFromShipment();

  // Returns if the broker has changed, hence we should reload
  async function show(): Promise<boolean> {
    let savedLocationId = findSavedLocationId(
      locationContext,
      shipment,
      savedLocations,
    );

    const originalLocation =
      locationContext === LocationContext.Pickup
        ? shipment.pickupLocation
        : shipment.deliveryLocation;

    const originalContact =
      locationContext === LocationContext.Pickup
        ? shipment.pickupContact
        : shipment.deliveryContact;

    // We need to show a screen that allows them to select from any of the saved brokers,
    // and optionally modify one of them or add a new one
    // const newSavedBrokerId = await showSwitchBrokerDialog(currentBroker);
    const updatedLocation = await modifyAddressDialog.show(originalLocation);

    if (updatedLocation === undefined) {
      // Cancel pressed
      return false;
    }

    function areAddressesEqual(location1: Location, location2: Location) {
      // Note the email address is missing here. We don't put the
      // contact's email on BOLs or send to the carrier.
      // So if that's all that has changed
      return (
        location1.businessName === location2.businessName &&
        location1.address.addressLine === location2.address.addressLine &&
        location1.address.addressLine2 === location2.address.addressLine2 &&
        location1.address.city === location2.address.city &&
        location1.address.countryCode === location2.address.countryCode &&
        location1.address.postalCode === location2.address.postalCode &&
        location1.address.stateOrProvinceCode ===
          location2.address.stateOrProvinceCode
      );
    }

    if (areAddressesEqual(updatedLocation, originalLocation)) {
      // Nothing changed
      return false;
    }

    setLoading(true);

    if (savedLocationId === undefined) {
      savedLocationId = await createSavedLocation(
        updatedLocation,
        originalContact,
      );
    } else {
      await savedLocationsService.modifyLocation(
        savedLocationId,
        updatedLocation,
      );
    }

    // Make an API call to change the broker
    await shipmentService.updateLocation(
      shipment.shipmentId,
      locationContext,
      updatedLocation,
    );
    setLoading(false);

    // If the shipment is in transit, then it's too late to give the driver
    // a new BOL. But if it's still showing as pending pickup, it'd worth showing them this screen
    if (shipment.shipmentState !== ShipmentState.InTransit) {
      await updatedBOLDialog.show();
    }

    return true;
  }

  const dialogs = (
    <>
      {loadingOverlay}
      {modifyAddressDialog.dialogs}
      {updatedBOLDialog.dialogs}
    </>
  );

  return { show, dialogs };
}
